/**
 * Hero Carousel Block
 * Full-width image carousel at top of homepage
 * 
 * Features:
 * - Autoplay every 5s with pause on hover
 * - Slide animation with loop
 * - Navigation arrows and dots
 * - Swipe support on mobile
 * - Pause when page is hidden (visibility API)
 * - Respects prefers-reduced-motion
 * - Link routing based on link_type (comic, series, novel, external)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { resolveImageUrl } from './shared';

const HeroCarouselBlock = ({ data = [], settings = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const carouselRef = useRef(null);
  const navigate = useNavigate();
  
  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;
  
  const slides = data || [];
  
  // Settings with defaults matching the spec
  // Support both camelCase and snake_case for flexibility
  const {
    autoplay = settings.auto_play ?? true,
    interval_ms = settings.interval ?? 5000,
    transition = 'slide',
    loop = true,
    pause_on_hover = true,
    show_arrows = settings.show_navigation ?? true,
    show_dots = settings.show_dots ?? true,
    swipe = true,
  } = settings;
  
  // Effective autoplay (disabled if reduced motion or only 1 slide)
  const effectiveAutoplay = autoplay && !prefersReducedMotion && slides.length > 1;
  
  // Page visibility handling
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  // Autoplay timer
  useEffect(() => {
    if (!effectiveAutoplay || isPaused || !isPageVisible || slides.length <= 1) return;
    
    const timer = setInterval(() => {
      goToNextSlide();
    }, interval_ms);
    
    return () => clearInterval(timer);
  }, [effectiveAutoplay, isPaused, isPageVisible, interval_ms, slides.length, currentSlide]);
  
  const goToNextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    setCurrentSlide((prev) => {
      if (prev >= slides.length - 1) {
        return loop ? 0 : prev;
      }
      return prev + 1;
    });
    
    setTimeout(() => setIsAnimating(false), 500);
  }, [slides.length, loop, isAnimating]);
  
  const goToPrevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    setCurrentSlide((prev) => {
      if (prev <= 0) {
        return loop ? slides.length - 1 : prev;
      }
      return prev - 1;
    });
    
    setTimeout(() => setIsAnimating(false), 500);
  }, [slides.length, loop, isAnimating]);
  
  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [currentSlide, isAnimating]);
  
  // Handle click navigation
  const handleSlideClick = (item) => {
    const linkType = item?.link_type;
    const linkTarget = item?.link_target;
    
    if (!linkType || !linkTarget) return;
    
    switch (linkType) {
      case 'comic':
        navigate(`/comics/${linkTarget}`);
        break;
      case 'series':
        navigate(`/series/${linkTarget}`);
        break;
      case 'novel':
        navigate(`/novels/${linkTarget}`);
        break;
      case 'external':
        window.open(linkTarget, '_blank', 'noopener,noreferrer');
        break;
      default:
        // Fallback to series if type not recognized
        if (linkTarget) {
          navigate(`/series/${linkTarget}`);
        }
    }
  };
  
  // Build link URL for <Link> component
  const getLinkUrl = (item) => {
    const linkType = item?.link_type;
    const linkTarget = item?.link_target;
    
    if (!linkType || !linkTarget) return '#';
    
    switch (linkType) {
      case 'comic':
        return `/comics/${linkTarget}`;
      case 'series':
        return `/series/${linkTarget}`;
      case 'novel':
        return `/novels/${linkTarget}`;
      case 'external':
        return linkTarget; // External URLs handled differently
      default:
        return `/series/${linkTarget}`;
    }
  };
  
  // Touch handling for swipe
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    if (!swipe) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    if (!swipe) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!swipe || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNextSlide();
    } else if (isRightSwipe) {
      goToPrevSlide();
    }
  };
  
  // Fallback: render nothing if no items
  if (slides.length === 0) {
    return null;
  }
  
  // Single slide: render static banner without autoplay/dots
  const isSingleSlide = slides.length === 1;
  const currentItem = slides[currentSlide];
  
  const heroBannerUrl = currentItem?.hero_banner_url;
  const fallbackCoverUrl = currentItem?.fallback_cover_url || currentItem?.cover_url;
  const hasHeroBanner = currentItem?.has_hero_banner || !!heroBannerUrl;
  
  const imageUrl = heroBannerUrl || 
    currentItem?.image_url || 
    resolveImageUrl(currentItem?.billboard_url) ||
    resolveImageUrl(fallbackCoverUrl);
  
  const title = currentItem?.title || '';
  const subtitle = currentItem?.subtitle || '';
  const creatorName = currentItem?.creator_name || '';
  const isExternal = currentItem?.link_type === 'external';
  const linkUrl = getLinkUrl(currentItem);
  
  // Transition classes
  const transitionClass = prefersReducedMotion 
    ? '' 
    : 'transition-all duration-500 ease-in-out';
  
  const SlideContent = () => (
    <>
      {/* Fallback rendering when no hero banner */}
      {!hasHeroBanner && fallbackCoverUrl ? (
        <div className="absolute inset-0">
          <div 
            className={`absolute inset-0 bg-cover bg-center scale-110 opacity-40 ${prefersReducedMotion ? '' : 'blur-xl'}`}
            style={{ backgroundImage: `url(${resolveImageUrl(fallbackCoverUrl)})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80" />
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={resolveImageUrl(fallbackCoverUrl)}
              alt={title}
              className="h-[85%] object-contain rounded-lg shadow-2xl"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent">
            <div className="absolute bottom-6 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
              {title && (
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 drop-shadow-lg">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm sm:text-base lg:text-lg text-gray-200 max-w-2xl line-clamp-2 drop-shadow">
                  {subtitle}
                </p>
              )}
              {creatorName && (
                <p className="text-sm text-orange-400 mt-2">
                  by {creatorName}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Normal hero banner rendering */
        <div className="absolute inset-0">
          <img
            src={imageUrl || 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=1920&h=1080&fit=crop'}
            alt={title}
            className={`w-full h-full object-cover ${transitionClass} ${!prefersReducedMotion ? 'group-hover:scale-105' : ''}`}
          />
          {title && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
              <div className="absolute bottom-6 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 drop-shadow-lg">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-sm sm:text-base lg:text-lg text-gray-200 max-w-2xl line-clamp-2 drop-shadow">
                    {subtitle}
                  </p>
                )}
                {creatorName && (
                  <p className="text-sm text-orange-400 mt-2">
                    by {creatorName}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <section 
      className="bg-black py-4 sm:py-6" 
      data-testid="hero-carousel-block"
      onMouseEnter={() => pause_on_hover && setIsPaused(true)}
      onMouseLeave={() => pause_on_hover && setIsPaused(false)}
    >
      <div className="container mx-auto px-2 sm:px-6">
        <div 
          ref={carouselRef}
          className="relative h-[280px] sm:h-[400px] lg:h-[500px] overflow-hidden rounded-none sm:rounded-xl"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Slide wrapper */}
          {isExternal ? (
            <a 
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 block cursor-pointer group"
              data-testid="hero-slide-link"
            >
              <SlideContent />
            </a>
          ) : (
            <Link 
              to={linkUrl}
              className="absolute inset-0 block cursor-pointer group"
              data-testid="hero-slide-link"
            >
              <SlideContent />
            </Link>
          )}

          {/* Navigation Arrows */}
          {show_arrows && !isSingleSlide && (
            <>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToPrevSlide();
                }}
                className={`absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full z-10 ${transitionClass}`}
                aria-label="Previous slide"
                data-testid="hero-carousel-prev"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToNextSlide();
                }}
                className={`absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full z-10 ${transitionClass}`}
                aria-label="Next slide"
                data-testid="hero-carousel-next"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {show_dots && !isSingleSlide && (
            <div 
              className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 sm:space-x-2 z-10"
              data-testid="hero-carousel-dots"
            >
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToSlide(index);
                  }}
                  className={`rounded-full ${transitionClass} ${
                    index === currentSlide 
                      ? 'w-4 sm:w-5 h-1 sm:h-1.5 bg-orange-500 opacity-60' 
                      : 'w-1 sm:w-1.5 h-1 sm:h-1.5 bg-gray-400 opacity-40 hover:opacity-60'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  data-testid={`hero-carousel-dot-${index}`}
                />
              ))}
            </div>
          )}
          
          {/* Slide counter for accessibility */}
          <div className="sr-only" aria-live="polite">
            Slide {currentSlide + 1} of {slides.length}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarouselBlock;
